/**
 * «Лиды»: вкладки (`?tab=`) — дневной отчёт (спека
 * `docs/specs/2026-09-24-leads-analytics-design.md`, числа как у
 * `prokat-runner leads-report`), «По дням» (`LeadDays`), «Источники» (`LeadSources`),
 * «По номерам» и «Очередь».
 */
import { useQuery } from "@tanstack/react-query";
import { CircleAlert, CircleCheck } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { useSearchParams } from "react-router";

import { LeadLink } from "@/components/LeadLink";
import { LeadAvitoSection } from "@/pages/LeadAvito";
import { LeadDaysSection } from "@/pages/LeadDays";
import { LeadInWorkSection } from "@/pages/LeadInWork";
import { LeadPhonesSection } from "@/pages/LeadPhones";
import { LeadQueueSection } from "@/pages/LeadQueue";
import { LeadSourcesSection } from "@/pages/LeadSources";
import { type Count, type LeadReport, loadLeadReport, shortTime, workTime, yesterday } from "@/lib/leads";

function Tile({ label, value, hint, children }: { label: string; value: ReactNode; hint?: string | undefined; children?: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4" data-annot={`плитка «${label}»`}>
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className="text-[26px] font-semibold leading-none tracking-[-0.02em] tabular-nums">{value}</div>
      {hint && <div className="text-[12px] text-fg-faint">{hint}</div>}
      {children}
    </div>
  );
}

/** Итог «да/нет» — всегда значком И словом, не одним цветом. */
function Verdict({ ok, yes, no }: { ok: boolean; yes: string; no: string }) {
  return ok ? (
    <span className="inline-flex items-center gap-1 text-[12px] text-ok">
      <CircleCheck size={13} /> {yes}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[12px] text-warn">
      <CircleAlert size={13} /> {no}
    </span>
  );
}

/** Разбивка с полоской доли: один ряд — одна категория, значение подписано текстом. */
function Breakdown({ title, rows, total }: { title: string; rows: Count[]; total: number }) {
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4" data-annot={`разбивка «${title}»`}>
      <h2 className="mb-3 text-[13px] font-medium">{title}</h2>
      {rows.length === 0 ? (
        <div className="text-[12.5px] text-fg-faint">нет</div>
      ) : (
        <table className="w-full text-[12.5px]">
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} title={`${r.name}: ${r.count} из ${total}`} data-annot={r.name}>
                <td className="w-[40%] truncate py-1 pr-3 text-fg-muted">{r.name}</td>
                <td className="py-1">
                  <div className="h-2 rounded-r-[4px] bg-accent" style={{ width: `${total ? (r.count / total) * 100 : 0}%` }} />
                </td>
                <td className="w-12 py-1 pl-3 text-right tabular-nums">{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Report({ r }: { r: LeadReport }) {
  const { new_leads: nl, incoming: inc, first_call: fc, coverage: cov } = r;
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Tile label="Новых лидов" value={nl.total} hint={`создано ${r.day.split("-").reverse().join(".")}`} />
        <Tile
          label="Входящих звонков"
          value={inc.total}
          hint={`отвечено ${inc.answered}, пропущено ${inc.missed}; номеров ${inc.unique_phones}`}
        />
        <Tile
          label="До первого исходящего, медиана"
          value={workTime(fc.median)}
          hint={`среднее ${workTime(fc.mean)} · p90 ${workTime(fc.p90)} · рабочее время ${r.work_hours}`}
        />
        <Tile label="Без исходящего до сих пор" value={fc.without_call} hint={`из ${fc.leads} лидов, которым нужно звонить`} />
        <Tile
          label="Обзвонены до конца смены"
          value={`${cov.called} из ${cov.total}`}
          hint={cov.without_phone ? `ещё ${cov.without_phone} без телефона — не считаем` : undefined}
        >
          <Verdict ok={cov.all_called} yes="все обзвонены" no="не все" />
        </Tile>
        <Tile label="Обработаны (звонок или входящий разговор)" value={`${cov.processed} из ${cov.total}`}>
          <Verdict ok={cov.all_processed} yes="все обработаны" no="не все" />
        </Tile>
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-3">
        <Breakdown title="Новые лиды по источнику" rows={nl.by_source} total={nl.total} />
        <Breakdown title="Новые лиды по статусу" rows={nl.by_status_group} total={nl.total} />
        <Breakdown title="Необзвонённые по источнику" rows={cov.missed_by_source} total={cov.missed.length} />
      </div>

      <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="необзвонённые">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border px-4 py-3">
          <h2 className="text-[13px] font-medium">Необзвонённые: {cov.missed.length}</h2>
          <span className="text-[12px] text-fg-faint">
            лиды с {shortTime(cov.window_from)} до {shortTime(cov.window_to)} без исходящего до конца смены (ищем по
            лиду и по номеру); спам, дубли и сервисные исключены; без телефона не считаем — {cov.without_phone}; с
            входящим разговором — {cov.missed_with_incoming_talk}
          </span>
        </div>
        {cov.missed.length === 0 ? (
          <div className="p-4 text-[12.5px] text-fg-muted">Всем позвонили вовремя.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="text-left text-[11.5px] text-fg-faint">
                <tr>
                  {["Лид", "Создан", "Источник", "Телефон", "Статус", "След. обработка", "Исходящий", "Входящий разговор"].map((h) => (
                    <th key={h} className="whitespace-nowrap px-4 py-2 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cov.missed.map((m) => (
                  <tr key={m.lead_id} className="border-t border-border hover:bg-bg-hover" data-annot={`лид ${m.lead_id}`}>
                    <td className="px-4 py-1.5 tabular-nums"><LeadLink id={m.lead_id} /></td>
                    <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{shortTime(m.created)}</td>
                    <td className="px-4 py-1.5">{m.source}</td>
                    <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{m.phone}</td>
                    <td className="px-4 py-1.5">{m.status_name}</td>
                    <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">{shortTime(m.next_work)}</td>
                    <td className="whitespace-nowrap px-4 py-1.5 tabular-nums text-fg-muted">
                      {m.first_out ? `позже: ${shortTime(m.first_out)}` : "не было"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">
                      {m.first_in_answered ? (
                        <span className={m.talked_in_time ? "" : "text-fg-muted"}>
                          {shortTime(m.first_in_answered)}
                          {m.talked_in_time ? "" : " (после смены)"}
                        </span>
                      ) : (
                        <span className="text-fg-faint">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

const TABS = [
  { id: "report", label: "Лиды" },
  { id: "days", label: "По дням" },
  { id: "sources", label: "Источники" },
  { id: "phones", label: "По номерам" },
  { id: "queue", label: "Очередь" },
  { id: "inwork", label: "В работе" },
  { id: "avito", label: "Лиды Авито" },
  { id: "vk", label: "Лиды ВК" },
] as const;
type Tab = (typeof TABS)[number]["id"];

function ReportTab() {
  const [day, setDay] = useState(yesterday);
  const report = useQuery({ queryKey: ["leads-report", day], queryFn: () => loadLeadReport(day) });
  return (
    <>
      <div className="flex flex-wrap items-center gap-3" data-annot="день отчёта">
        <label className="ml-auto flex items-center gap-2 text-[12.5px] text-fg-muted">
          День
          <input
            type="date"
            value={day}
            max={new Date().toLocaleDateString("sv-SE")}
            onChange={(e) => e.target.value && setDay(e.target.value)}
            className="h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-fg"
          />
        </label>
      </div>
      {report.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Считаю по CRM…</div>
      ) : report.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить отчёт: {String(report.error.message)}</div>
      ) : (
        <Report r={report.data} />
      )}
    </>
  );
}

export function LeadsPage() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.some((t) => t.id === params.get("tab")) ? (params.get("tab") as Tab) : "report";
  const select = (id: Tab) => {
    const out = new URLSearchParams(params);
    if (id === "report") out.delete("tab");
    else out.set("tab", id);
    setParams(out, { replace: true });
  };

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-2 border-b border-border" data-annot="шапка лидов">
        <h1 className="pb-2 text-[16px] font-semibold tracking-[-0.015em]">Обработка лидов</h1>
        <div role="tablist" aria-label="Разделы лидов" className="flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => select(t.id)}
              className={`-mb-px border-b-2 px-3 pb-2 pt-1 text-[13px] ${
                tab === t.id ? "border-accent font-medium text-fg" : "border-transparent text-fg-muted hover:text-fg"
              }`}
              data-annot={`вкладка «${t.label}»`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {tab === "report" && <ReportTab />}
      {tab === "days" && <LeadDaysSection />}
      {tab === "sources" && <LeadSourcesSection />}
      {tab === "phones" && <LeadPhonesSection />}
      {tab === "queue" && <LeadQueueSection />}
      {tab === "inwork" && <LeadInWorkSection />}
      {tab === "avito" && <LeadAvitoSection key="avito" />}
      {tab === "vk" && <LeadAvitoSection key="vk" source="vkgroup" />}
    </main>
  );
}
